import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Box, Typography, Button, CircularProgress } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import ReLoginModal from '../../ReLoginModal';

interface CustomerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (customer: Customer) => void;
}

interface Customer {
  _id: string;
  businessName: string;
  customerRef: string; 
}

type ApiError = {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
};

const CustomerModal: React.FC<CustomerModalProps> = ({ open, onClose, onSelect }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reLogin, setReLogin] = useState(false);

  const fetchCustomers = useCallback(async () => {
    setIsLoading(true);
    const token = localStorage.getItem('token');
    if (!token) {
      setReLogin(true);
      setIsLoading(false);
      return;
    }

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await axios.get('https://apibiz.oneflaretech.com/api/customer', { headers });
      setCustomers(response.data.data || []);
      // console.log('Fetched customers:', response.data.data);
    } catch (error) {
      const err = error as ApiError;
      const errorMessage = err.response?.data?.message || 'Failed to load customers';
      if (errorMessage.includes('jwt expired') || errorMessage.includes('jwt malformed')) {
        setReLogin(true);
      } else {
        toast.error(errorMessage);
      }
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchCustomers();
    }
  }, [open, fetchCustomers]);

  const handleSelectCustomer = (customer: Customer) => {
    onSelect(customer);
    onClose();
  };

  return (
    <>
      {reLogin && <ReLoginModal />}
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'white',
            boxShadow: 24,
            p: 4,
            minWidth: 400,
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            Select Customer
          </Typography>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          ) : (
            <div>
              {customers.length > 0 ? (
                customers.map((customer) => (
                  <Button
                    key={customer._id}
                    variant="outlined"
                    fullWidth={true} // Restored fullWidth for consistency
                    sx={{ mb: '1' }}
                    onClick={() => handleSelectCustomer(customer)}
                  >
                    {customer.businessName}
                  </Button>
                ))
              ) : (
                <Typography>No customers available</Typography>
              )}
            </div>
          )}
          <Button variant="contained" color="primary" onClick={onClose} sx={{ mt: 2 }}>
            Cancel
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default CustomerModal;